import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ClipboardCheck, FileText } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DocumentViewer } from "../components/DocumentViewer";
import { assetPath } from "../components/register/exports";
import { SourceLink } from "../components/SourceLink";
import { Empty, ErrorState, Loading } from "../components/States";
import { type ReviewDecision, type ReviewItem as Item, type ReviewShown } from "../lib/api";
import { ApiError } from "../lib/http";
import { useMe, type Me } from "../lib/me";
import { fieldLabel } from "../lib/words";
import {
  basename, DecidedLine, DecisionButtons, DecisionForm, REASON_LABELS, REASON_TONE, Shown, SOURCE_LABELS, when,
} from "./Review";
import { documentOpenPath } from "./documentViewFlow";
import { decideReview, fetchReview, REVIEWS_KEY } from "./reviewFlow";

type Box = { x: number; y: number; width: number; height: number };

/** A page the item rests on, to open with its region drawn when one was located. */
export interface Citation {
  label: string;
  document: string;
  page: number;
  box: Box | null;
}

/** Every page an approval of this item rests on. Only what the API cited; nothing is guessed. */
export function evidenceOf(shown: ReviewShown | null | undefined): Citation[] {
  if (!shown) return [];
  if (shown.gate === "confirm_documents") {
    return (shown.documents ?? []).map((d) => ({ label: basename(d), document: d, page: 1, box: null }));
  }
  if (shown.fields) {
    return shown.fields.flatMap((f) =>
      f.document && f.page
        ? [{
            label: `${f.name ? fieldLabel(f.name) : "Value"}: ${f.value ?? "(empty)"}`,
            document: f.document, page: f.page, box: f.box ?? null,
          }]
        : [],
    );
  }
  if (shown.document && shown.page) {
    return [{ label: shown.value ?? "(empty)", document: shown.document, page: shown.page, box: shown.box ?? null }];
  }
  return [];
}

/** The screen the item came from, where its full context lives. */
export function sourceOf(item: Item): { to: string; label: string } | null {
  if (item.source_type === "module_run") return { to: `/modules/runs/${item.source_id}`, label: "Open the module run" };
  if (item.source_type === "uc5_job") return { to: `/vendor/${item.source_id}`, label: "Open the vendor job" };
  const job = item.shown?.job_id;
  if (item.source_type === "uc1_extraction" && job) {
    return {
      to: `/extraction/${job}`,
      label: item.reason === "conflict" ? "Choose the value on the extraction" : "Open the extraction",
    };
  }
  return null;
}

/** One item, its evidence, and its decision or the record of it. */
export function ReviewDetail({ item, me, busy, error, onDecide, onOpen }: {
  item: Item;
  me: Me | null | undefined;
  busy: boolean;
  error: string | null;
  onDecide: (decision: ReviewDecision, note: string) => void;
  onOpen: (c: Citation) => void;
}) {
  const [draft, setDraft] = useState<ReviewDecision | null>(null);
  const cites = evidenceOf(item.shown);
  const source = sourceOf(item);
  const url = item.shown?.options?.[0]?.source_url ?? item.shown?.source_url;

  return (
    <div className="stack">
      <section className="card">
        <dl className="facts">
          <dt>Source</dt><dd>{SOURCE_LABELS[item.source_type] ?? item.source_type}</dd>
          <dt>Tag</dt>
          <dd className="mono">
            {item.tag && item.package
              ? <Link to={assetPath(item.package, item.tag)}>{item.tag}</Link>
              : (item.tag ?? "-")}
          </dd>
          <dt>Package</dt><dd>{item.package ?? "-"}</dd>
          {item.field && <><dt>Field</dt><dd>{fieldLabel(item.field)}</dd></>}
          <dt>Reason</dt>
          <dd><span className={`pill ${REASON_TONE[item.reason] ?? "info"}`}>{REASON_LABELS[item.reason] ?? item.reason}</span></dd>
          <dt>Flagged</dt><dd>{when(item.flagged_at)}</dd>
          <dt>{item.state === "pending" ? "Under review" : "What was shown"}</dt><dd><Shown shown={item.shown} /></dd>
        </dl>
        {source && <p style={{ marginTop: 16 }}><Link to={source.to}>{source.label}</Link></p>}
      </section>

      <section className="card">
        <div className="section-title"><h3>Evidence</h3></div>
        {cites.length === 0 && !url && <p className="muted">No page is cited for this item.</p>}
        {(cites.length > 0 || url) && (
          <ul className="evidence">
            {cites.map((c) => (
              <li key={`${c.document}:${c.page}:${c.label}`}>
                <button className="ghost" onClick={() => onOpen(c)}>
                  <span className="row" style={{ gap: 6 }}><FileText size={15} aria-hidden="true" /> Open page {c.page}</span>
                </button>
                <Link className="mono" style={{ fontSize: 13 }} to={documentOpenPath({ path: c.document, pkg: item.package, page: c.page }) ?? "/documents"}
                      title="Open in the document view">{basename(c.document)}</Link>
                <span className="muted" style={{ fontSize: 13 }}>{c.box ? c.label : `${c.label} (location not marked on the page)`}</span>
              </li>
            ))}
            {url && <li><SourceLink url={url} pkg={item.package} /></li>}
          </ul>
        )}
      </section>

      <section className="card">
        <div className="section-title"><h3>Decision</h3></div>
        {error && <p role="alert" style={{ color: "var(--color-fail)", marginBottom: 12 }}>{error}</p>}
        {item.state === "pending" ? (
          draft ? (
            <DecisionForm decision={draft} busy={busy} onCancel={() => setDraft(null)}
                          onSubmit={(note) => onDecide(draft, note)} />
          ) : (
            <DecisionButtons me={me} item={item} busy={busy} onPick={setDraft} />
          )
        ) : (
          <DecidedLine item={item} />
        )}
      </section>
    </div>
  );
}

/** /review/:id. A 404 means no such item, or one in a package this person cannot read. */
export function ReviewItem() {
  const { id = "" } = useParams();
  const { me } = useMe();
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Citation | null>(null);
  const query = useQuery({ queryKey: [...REVIEWS_KEY, "item", id], queryFn: () => fetchReview(id) });

  async function decide(decision: ReviewDecision, note: string) {
    setBusy(true); setError(null);
    try {
      const decided = await decideReview(id, decision, note);
      client.setQueryData([...REVIEWS_KEY, "item", id], decided);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      void client.invalidateQueries({ queryKey: REVIEWS_KEY });
    }
  }

  const missing = query.error instanceof ApiError && [404, 422].includes(query.error.status);
  return (
    <div className="page">
      <p style={{ marginBottom: 12 }}>
        <Link to="/review" className="row" style={{ gap: 6 }}><ArrowLeft size={15} aria-hidden="true" /> Review queue</Link>
      </p>
      <h2 style={{ fontSize: 26, marginBottom: 16 }}>
        <span className="row"><ClipboardCheck size={24} /> Review item</span>
      </h2>
      {missing ? (
        <Empty title="No review item at this address">
          It may be in a package you cannot read, or the link may be wrong.
        </Empty>
      ) : query.isPending ? (
        <Loading what="Loading the review item" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <ReviewDetail item={query.data} me={me} busy={busy} error={error} onDecide={decide} onOpen={setOpen} />
      )}
      {open && (
        <DocumentViewer pkg={query.data?.package ?? null} path={open.document} page={open.page} box={open.box} label={open.label}
                        onClose={() => setOpen(null)} />
      )}
    </div>
  );
}
